import { useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

const PRELOAD_MAX_AGE = 5 * 60 * 1000; // 5 mins
const TIME_TO_INTENT = 200;
const PRELOAD_DISPOSE_DELAY = 1000;

export type EntryPointPreloadActions<T> = {
	/** Cancel pending preload; debounce reference disposal when the lifecycle gate is enabled. */
	cancelPreload: () => void;
	/**
	 * Eagerly load the entry point immediately (cancels any pending preload timeouts).
	 * Returns the loaded reference wrapper with a `dispose` function.
	 */
	eagerLoad: () => { dispose: () => void; reference: T };
	/** Start preloading with an intent delay (TIME_TO_INTENT ms). No-op if already preloading. */
	preloadReference: () => void;
};

/**
 * Low-level hook that manages the preload / eager-load lifecycle for an entry
 * point reference.  It does **not** bind any DOM events — callers wire the
 * returned callbacks to whatever interaction pattern they need (e.g. JSX event
 * props, a ref-callback with `bind-event-listener`, etc.).
 *
 * This is the shared core extracted from `usePressablePreloadRef` so the same
 * logic can be reused in contexts that don't need the ref-callback pattern.
 */
export function useEntryPointPreload<T extends { dispose: () => void }>(
	load: () => T,
): EntryPointPreloadActions<T> {
	const actions = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadDisposeTimeout?: ReturnType<typeof setTimeout>;
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadIntentTimeout = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}
		};

		const clearPreloadTimeouts = () => {
			clearPreloadIntentTimeout();

			if (fg('platform_relay_entrypoint_preload_lifecycle')) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			} else if (
				typeof request.preloadMaxAgeTimeout === 'number' &&
				fg('platform_relay_preload_ensure_max_age_disposal') === false
			) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreloadDisposal = () => {
			if (request.preloadDisposeTimeout !== undefined) {
				clearTimeout(request.preloadDisposeTimeout);
				delete request.preloadDisposeTimeout;
			}
		};

		const disposePreload = () => {
			if (fg('platform_relay_entrypoint_preload_lifecycle')) {
				// Keep max-age eviction active until the preload is disposed or consumed.
				clearPreloadIntentTimeout();
				cancelPreloadDisposal();

				if (request.preload) {
					request.preloadDisposeTimeout = setTimeout(() => {
						request.preload?.dispose();
						clearPreloadTimeouts();
					}, PRELOAD_DISPOSE_DELAY);
				}
			} else {
				clearPreloadTimeouts();
			}
		};

		const cleanupPreload = () => {
			if (fg('platform_relay_entrypoint_preload_lifecycle')) {
				cancelPreloadDisposal();
				clearPreloadTimeouts();
			}

			request.preload?.dispose();

			// Preserve legacy max-age cleanup when the lifecycle gate is disabled.
			if (
				typeof request.preloadMaxAgeTimeout === 'number' &&
				fg('platform_relay_preload_ensure_max_age_disposal')
			) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const eagerLoad = () => {
			clearPreloadTimeouts();
			if (fg('platform_relay_entrypoint_preload_lifecycle')) {
				cancelPreloadDisposal();
			}
			return loadReference();
		};

		const preloadReference = () => {
			if (fg('platform_relay_entrypoint_preload_lifecycle')) {
				cancelPreloadDisposal();
			}

			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					if (fg('platform_relay_entrypoint_preload_lifecycle')) {
						cancelPreloadDisposal();
					}
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		return {
			preloadReference,
			cancelPreload: disposePreload,
			cleanupPreload,
			eagerLoad,
		};
	}, [load]);

	// Dispose preload and clear timeouts when component unmounts
	// or when `load` identity changes (new actions object created).
	useEffect(() => {
		return () => {
			actions.cleanupPreload();
		};
	}, [actions]);

	return actions;
}
